import { useState, useEffect, useRef } from 'react';
import { Package, Warehouse, Store, Check, AlertCircle, X, ChevronRight, Camera } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import api from '../lib/api';
import { getProductByBarcode, getCategories } from '../lib/api';
import { BarcodeScanner } from './BarcodeScanner';

const LoadingModal = ({ isOpen, onClose, onComplete }) => {
    const [step, setStep] = useState(1); // 1 = scan, 2 = destination, 3 = confirm
    const [loading, setLoading] = useState(false);
    const [showCameraScanner, setShowCameraScanner] = useState(false);
    const [needsScan, setNeedsScan] = useState(false);
    
    // Product state
    const [barcode, setBarcode] = useState('');
    const [product, setProduct] = useState(null);
    const [isNewProduct, setIsNewProduct] = useState(false);
    const [productName, setProductName] = useState('');
    const [productCategory, setProductCategory] = useState('');
    const [productPrice, setProductPrice] = useState('');
    const [quantity, setQuantity] = useState('1');
    
    // Destination
    const [destination, setDestination] = useState(null); // 'depozit' or 'magazin'
    const [depozitLocation, setDepozitLocation] = useState('P01');
    const [existingLocations, setExistingLocations] = useState([]);
    
    // Categories
    const [categories, setCategories] = useState([]);
    const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
    
    const barcodeInputRef = useRef(null);
    
    useEffect(() => {
        if (isOpen) {
            loadCategories();
            loadDepozitLocations();
            setTimeout(() => barcodeInputRef.current?.focus(), 100);
        }
    }, [isOpen]);
    
    useEffect(() => {
        if (!isOpen) {
            resetForm();
        }
    }, [isOpen]);
    
    const loadCategories = async () => {
        try {
            const response = await getCategories();
            setCategories(response.data);
        } catch (err) {
            console.error('Error loading categories:', err);
        }
    };
    
    const loadDepozitLocations = async () => {
        try {
            const response = await api.get('/depozit/locations');
            setExistingLocations(response.data || []);
        } catch (err) {
            console.error('Error loading locations:', err);
        }
    };
    
    const resetForm = () => {
        setStep(1);
        setBarcode('');
        setProduct(null);
        setIsNewProduct(false);
        setProductName('');
        setProductCategory('');
        setProductPrice('');
        setQuantity('1');
        setDestination(null);
        setDepozitLocation('P01');
        setLoading(false);
    };
    
    const resetForNextProduct = () => {
        setStep(1);
        setBarcode('');
        setProduct(null);
        setIsNewProduct(false);
        setProductName('');
        setProductCategory('');
        setProductPrice('');
        setQuantity('1');
        // Keep destination and location for faster workflow
        setLoading(false);
        setNeedsScan(true);
        setShowCameraScanner(false);
        // Reload categories to include any new ones
        loadCategories();
        setTimeout(() => barcodeInputRef.current?.focus(), 100);
    };
    
    // Handle camera scan
    const handleCameraScan = async (scannedCode) => {
        setShowCameraScanner(false);
        setBarcode(scannedCode);
        setNeedsScan(false);
        // Trigger lookup
        setLoading(true);
        try {
            const response = await getProductByBarcode(scannedCode);
            if (response.data) {
                setProduct(response.data);
                setProductName(response.data.name);
                setProductCategory(response.data.category || '');
                setProductPrice(response.data.price?.toString() || '0');
                setIsNewProduct(false);
                setStep(2);
            }
        } catch {
            setIsNewProduct(true);
            setProductName('');
            setProductCategory('');
            setProductPrice('');
        } finally {
            setLoading(false);
        }
    };
    
    const lookupProduct = async () => {
        if (!barcode.trim()) return;
        
        setLoading(true);
        try {
            const response = await getProductByBarcode(barcode.trim());
            if (response.data) {
                setProduct(response.data);
                setProductName(response.data.name);
                setProductCategory(response.data.category || '');
                setProductPrice(response.data.price?.toString() || '0');
                setIsNewProduct(false);
                setStep(2);
            }
        } catch (err) {
            if (err.response?.status === 404) {
                setIsNewProduct(true);
                setProductName('');
                setProductCategory('');
                setProductPrice('');
                setStep(2);
            } else {
                toast.error('Eroare la căutarea produsului');
            }
        } finally {
            setLoading(false);
        }
    };
    
    const handleBarcodeKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            lookupProduct();
        }
    };
    
    const handleSelectDestination = async (dest) => {
        setDestination(dest);
        
        // For magazin - complete directly without confirmation step
        if (dest === 'magazin') {
            if (!productName.trim()) {
                toast.error('Introdu numele produsului');
                return;
            }
            
            setLoading(true);
            try {
                const qty = parseInt(quantity) || 1;
                let productId = product?.id;
                
                // Create product if new
                if (isNewProduct) {
                    const createRes = await api.post('/products', {
                        name: productName,
                        barcode: barcode,
                        price: parseFloat(productPrice) || 0,
                        category: productCategory
                    });
                    productId = createRes.data.id;
                }
                
                await api.post('/store', {
                    product_id: productId,
                    quantity: qty
                });
                toast.success(`${productName} x${qty} → Magazin`);
                
                if (onComplete) onComplete();
                resetForNextProduct();
                // Auto-open camera for next scan
                setShowCameraScanner(true);
                
            } catch (err) {
                console.error('Error:', err);
                toast.error(err.response?.data?.detail || 'Eroare la adăugare');
            } finally {
                setLoading(false);
            }
        } else {
            // For depozit - go to step 3 for location selection
            setStep(3);
        }
    };
    
    const handleComplete = async () => {
        if (!productName.trim()) {
            toast.error('Introdu numele produsului');
            return;
        }
        
        setLoading(true);
        try {
            const qty = parseInt(quantity) || 1;
            let productId = product?.id;
            
            // Create product if new
            if (isNewProduct) {
                const createRes = await api.post('/products', {
                    name: productName,
                    barcode: barcode,
                    price: parseFloat(productPrice) || 0,
                    category: productCategory
                });
                productId = createRes.data.id;
            }
            
            // Only depozit uses this function now (magazin is handled in handleSelectDestination)
            await api.post('/depozit', {
                product_id: productId,
                quantity: qty,
                location: depozitLocation
            });
            toast.success(`${productName} x${qty} → Depozit (${depozitLocation})`);
            
            if (onComplete) onComplete();
            
            // Reset for next product and auto-open camera
            resetForNextProduct();
            setShowCameraScanner(true);
            
        } catch (err) {
            console.error('Error:', err);
            toast.error(err.response?.data?.detail || 'Eroare la adăugare');
        } finally {
            setLoading(false);
        }
    };
    
    const filteredCategories = categories.filter(cat => 
        cat.toLowerCase().includes(productCategory.toLowerCase())
    );
    
    if (!isOpen) return null;
    
    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Package className="text-blue-500" size={20} />
                        Încărcare Marfă
                    </DialogTitle>
                </DialogHeader>
                
                {/* Progress */}
                <div className="flex items-center gap-2 mb-4 text-xs">
                    <span className={`px-2 py-1 rounded ${step >= 1 ? 'bg-blue-500 text-white' : 'bg-zinc-200'}`}>
                        1. Scanare
                    </span>
                    <ChevronRight size={14} className="text-zinc-300" />
                    <span className={`px-2 py-1 rounded ${step >= 2 ? 'bg-blue-500 text-white' : 'bg-zinc-200'}`}>
                        2. Destinație
                    </span>
                    <ChevronRight size={14} className="text-zinc-300" />
                    <span className={`px-2 py-1 rounded ${step >= 3 ? 'bg-blue-500 text-white' : 'bg-zinc-200'}`}>
                        3. Confirmare
                    </span>
                </div>
                
                {step === 1 ? (
                    /* Step 1: Scan Barcode */
                    <div className="space-y-4">
                        {needsScan && (
                            <div className="flex items-center gap-2 text-emerald-600 bg-emerald-50 rounded-lg p-3">
                                <Check size={18} />
                                <span className="text-sm">Produs adăugat! Scanează următorul sau închide.</span>
                            </div>
                        )}
                        
                        <div>
                            <Label>Cod de bare</Label>
                            <div className="flex gap-2">
                                <Input
                                    ref={barcodeInputRef}
                                    value={barcode}
                                    onChange={(e) => setBarcode(e.target.value)}
                                    onKeyDown={handleBarcodeKeyDown}
                                    placeholder="Scanează sau introdu codul"
                                    autoFocus
                                    className="flex-1"
                                />
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setShowCameraScanner(true)}
                                    className="px-3"
                                    title="Scanează cu camera"
                                >
                                    <Camera size={20} />
                                </Button>
                            </div>
                        </div>
                        
                        <Button 
                            onClick={lookupProduct}
                            disabled={loading || !barcode.trim()}
                            className="w-full bg-blue-500 hover:bg-blue-600"
                        >
                            {loading ? 'Se caută...' : 'Caută Produs'}
                        </Button>
                        
                        <p className="text-xs text-center text-zinc-400">
                            Apasă Enter sau butonul pentru a căuta
                        </p>
                    </div>
                ) : step === 2 ? (
                    /* Step 2: Product Info + Destination */
                    <div className="space-y-4">
                        <div className="bg-zinc-50 rounded-lg p-3">
                            <p className="text-xs text-zinc-500">Cod: {barcode}</p>
                            {isNewProduct ? (
                                <p className="text-amber-600 text-sm font-medium">Produs nou</p>
                            ) : (
                                <p className="text-green-600 text-sm font-medium">{productName}</p>
                            )}
                        </div>
                        
                        {isNewProduct && (
                            <>
                                <div>
                                    <Label>Nume Produs *</Label>
                                    <Input
                                        value={productName}
                                        onChange={(e) => setProductName(e.target.value)}
                                        placeholder="Denumire produs"
                                        autoFocus
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label>Categorie</Label>
                                        <Input
                                            value={productCategory}
                                            onChange={(e) => setProductCategory(e.target.value)}
                                            placeholder="Categorie"
                                        />
                                    </div>
                                    <div>
                                        <Label>Preț (RON)</Label>
                                        <Input
                                            type="number"
                                            value={productPrice}
                                            onChange={(e) => setProductPrice(e.target.value)}
                                            placeholder="0"
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                        
                        <div>
                            <Label>Cantitate</Label>
                            <Input
                                type="number"
                                min="1"
                                value={quantity}
                                onChange={(e) => setQuantity(e.target.value)}
                            />
                        </div>
                        
                        <p className="text-sm text-zinc-600 text-center">Unde plasezi produsul?</p>
                        
                        <div className="grid grid-cols-2 gap-3">
                            <Button
                                variant="outline"
                                className="h-20 flex flex-col items-center justify-center gap-2 hover:bg-blue-50 hover:border-blue-300"
                                onClick={() => handleSelectDestination('depozit')}
                            >
                                <Warehouse className="text-blue-600" size={24} />
                                <span className="font-semibold">Depozit</span>
                            </Button>
                            
                            <Button
                                variant="outline"
                                className="h-20 flex flex-col items-center justify-center gap-2 hover:bg-emerald-50 hover:border-emerald-300"
                                onClick={() => handleSelectDestination('magazin')}
                            >
                                <Store className="text-emerald-600" size={24} />
                                <span className="font-semibold">Magazin</span>
                            </Button>
                        </div>
                        
                        <Button 
                            variant="outline" 
                            onClick={() => setStep(1)}
                            className="w-full"
                        >
                            Înapoi
                        </Button>
                    </div>
                ) : step === 3 ? (
                    /* Step 3: Confirm */
                    <div className="space-y-4">
                        <div className="bg-zinc-50 rounded-lg p-3">
                            <p className="font-semibold">{productName} x{quantity}</p>
                            <p className="text-sm text-zinc-500">
                                → {destination === 'depozit' ? 'Depozit' : 'Magazin'}
                            </p>
                        </div>
                        
                        {destination === 'depozit' && (
                            <div>
                                <Label>Locație în Depozit</Label>
                                <Input
                                    value={depozitLocation}
                                    onChange={(e) => setDepozitLocation(e.target.value.toUpperCase())}
                                    placeholder="P01"
                                />
                                {existingLocations.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mt-2">
                                        {existingLocations.map(loc => (
                                            <button
                                                key={loc}
                                                type="button"
                                                onClick={() => setDepozitLocation(loc)}
                                                className={`px-2 py-1 text-xs rounded border ${
                                                    depozitLocation === loc 
                                                        ? 'bg-blue-500 text-white border-blue-500' 
                                                        : 'bg-white border-zinc-300 hover:border-blue-300'
                                                }`}
                                            >
                                                {loc}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                        
                        <div className="flex gap-2">
                            <Button 
                                variant="outline" 
                                onClick={() => setStep(2)}
                                className="flex-1"
                            >
                                Înapoi
                            </Button>
                            <Button 
                                onClick={handleComplete}
                                disabled={loading}
                                className={`flex-1 ${destination === 'depozit' ? 'bg-blue-500 hover:bg-blue-600' : 'bg-emerald-500 hover:bg-emerald-600'}`}
                            >
                                {loading ? 'Se adaugă...' : 'Adaugă'}
                            </Button>
                        </div>
                        
                        <p className="text-xs text-center text-zinc-400">
                            După adăugare, poți continua cu următorul produs
                        </p>
                    </div>
                ) : null}
            </DialogContent>
            
            {/* Camera Scanner */}
            <BarcodeScanner
                isOpen={showCameraScanner}
                onClose={() => setShowCameraScanner(false)}
                onScan={handleCameraScan}
            />
        </Dialog>
    );
};

export default LoadingModal;
